import {
  NotificationType,
  Notification,
  isBankNotificationDetails,
  isSubmittedOrderNotificationDetails,
  isTransactionOrderNotificationDetails,
  isMandateNotificationDetails,
  isPlanStatusNotificationDetails,
} from "../api";
import { addFullStopToSentence } from "./string";

export const NOTIFICATION_TITLE: Record<NotificationType, string> = {
  CLIENT_CREATED: "Client signed up",
  CLIENT_CONSENT_RECORDED: "Consent recorded",
  CLIENT_ACTIVATED: "Onboarding complete",
  CLIENT_DETAILS_UPDATED: "Client details updated",
  BANK_ACCOUNT_VERIFIED: "Bank verified",
  BANK_ACCOUNT_VERIFICATION_FAILED: "Bank verification failed",
  KYC_VALIDATED: "KYC validated",
  KYC_FAILED: "KYC not registered",
  MANDATE_CREATED: "Mandate created",
  MANDATE_APPROVED: "Mandate approved",
  MANDATE_ACTIVATED: "Mandate activated",
  MANDATE_REJECTED: "Mandate rejected",
  MFCENTRAL_FETCH: "Holdings fetched",
  ORDER_SUBMITTED: "Order submitted",
  ORDER_SUBMISSION_FAILED: "Order submission failed",
  ORDER_PAYMENT_COMPLETED: "Payment complete",
  ORDER_PROCESSED: "Order complete",
  ORDER_PROCESSING_FAILED: "Order failed",
  STP_ACTIVATED: "STP activated",
  STP_CANCELLED: "STP cancelled",
  STP_COMPLETED: "STP completed",
  SWP_ACTIVATED: "SWP activated",
  SWP_CANCELLED: "SWP cancelled",
  SWP_COMPLETED: "SWP completed",
  XSIP_ACTIVATED: "SIP activated",
  XSIP_CANCELLED: "SIP cancelled",
  XSIP_COMPLETED: "SIP completed",
  XSIP_PAUSED: "SIP paused",
};

export const NOTIFICATION_DESCRIPTION: Record<
  NotificationType,
  (details: Notification["details"]) => string
> = {
  CLIENT_CREATED: () => "Finish setup or ask the client to proceed on the app.",
  CLIENT_CONSENT_RECORDED: () => "Finish setup to complete activation.",
  CLIENT_ACTIVATED: () => "Account activated. Start transactions now.",
  CLIENT_DETAILS_UPDATED: () => "Start placing transactions again.",
  BANK_ACCOUNT_VERIFIED: () => "",
  BANK_ACCOUNT_VERIFICATION_FAILED: (details) => {
    if (isBankNotificationDetails(details)) {
      return `${addFullStopToSentence(details?.bank_account?.verification_remark || "")} Ask client to review details.`;
    }
    return "";
  },
  KYC_VALIDATED: () => "",
  KYC_FAILED: () => "Ask client to check and register KYC again.",
  MANDATE_CREATED: () => "Awaiting authorization to start SIPs.",
  MANDATE_APPROVED: () => "Authorization successful. Start SIPs now.",
  MANDATE_ACTIVATED: () =>
    "Setup SIPs or pay for one-time transactions via mandate.",
  MANDATE_REJECTED: (details) => {
    if (isMandateNotificationDetails(details)) {
      return `${addFullStopToSentence(details.mandate.nse_rejection_reason || "")} Ask client to review and set it up again.`;
    }
    return "";
  },
  MFCENTRAL_FETCH: () => "External portfolio synced successfully.",
  ORDER_SUBMITTED: (details) => {
    if (isTransactionOrderNotificationDetails(details)) {
      const { orders } = details;
      return `${orders.length} order item(s) authorized successfully.`;
    }
    return "";
  },
  ORDER_SUBMISSION_FAILED: (details) => {
    if (isTransactionOrderNotificationDetails(details)) {
      const { orders } = details;
      return `Authorization for ${orders.length} order item(s) failed. Review details and try again.`;
    }
    return "";
  },
  ORDER_PAYMENT_COMPLETED: (details) => {
    if (isSubmittedOrderNotificationDetails(details)) {
      const { orders } = details;
      return `Payment for ${orders.length} order item(s) successful. View details in recent orders.`;
    }
    return "";
  },
  ORDER_PROCESSED: () => "See client holdings for updated units.",
  ORDER_PROCESSING_FAILED: (details) => {
    if (isSubmittedOrderNotificationDetails(details)) {
      const { orders } = details;
      return `Order processing for ${orders.length} order item(s) failed. View details in recent orders.`;
    }
    return "";
  },
  STP_ACTIVATED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} STP(s) activated. View client STPs for more details.`;
    }
    return "";
  },
  STP_CANCELLED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} STP(s) cancelled. View client STPs for more details.`;
    }
    return "";
  },
  STP_COMPLETED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} STP(s) completed. View client STPs for more details.`;
    }
    return "";
  },
  SWP_ACTIVATED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SWP(s) activated. View client SWPs for more details.`;
    }
    return "";
  },
  SWP_CANCELLED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SWP(s) cancelled. View client SWPs for more details.`;
    }
    return "";
  },
  SWP_COMPLETED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SWP(s) completed. View client SWPs for more details.`;
    }
    return "";
  },
  XSIP_ACTIVATED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SIP(s) activated. View client SIPs for more details.`;
    }
    return "";
  },
  XSIP_CANCELLED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SIP(s) cancelled. View client SIPs for more details.`;
    }
    return "";
  },
  XSIP_COMPLETED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SIP(s) completed. View client SIPs for more details.`;
    }
    return "";
  },
  XSIP_PAUSED: (details) => {
    if (isPlanStatusNotificationDetails(details)) {
      const { updates } = details;
      return `${updates.length} SIP(s) paused. View client SIPs for more details.`;
    }
    return "";
  },
};
